import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from './baseQuery';
import { TAG } from '@/constants';
import { buildQueryString } from '@/utils';
import { normalizeCalculatorPackage } from './calculatorPackageMappers';

const CALCULATOR_PACKAGES_URL = 'calculator-packages/api/v1';

/** Row shape aligned with backend; calculator fields + optional media-schedule fields */
export interface CalculatorPackageRow {
  country: string;
  channel: string;
  publisher: string;
  format: string;
  deliveryPercentage: number;
  rowCurrency: string;
  buyCPM: number;
  dspFeePercentage: number;
  /** DSP the row is bought through. Saved so a line priced from the package
   *  carries it as its platform; absent on packages saved before it was. */
  dspName?: string;
  // Optional media-schedule fields
  product?: string;
  kpis?: string[];
  formats?: string[];
  buyingMetric?: string;
  sellRate?: number;
  buyRate?: number;
  costsFee?: number;
  benchmark?: number;
  monthlySpent?: number;
}

export interface CalculatorPackage {
  _id: string;
  name: string;
  description?: string;
  channel: string;
  /** Total budget used when building the package (plan total) */
  totalBudget?: number;
  /** Total hard costs used in builder */
  hardCostsTotal?: number;
  /** Trading deal / rebate percentage used in builder */
  tradingDealPercentage?: number;
  /** FX rates used in builder (base GBP) */
  fxRates?: Record<string, number>;
  /** Package-level Sell CPM */
  sellCpm?: number;
  /** Package-level currency for Sell CPM / blended metrics */
  currency?: string;
  /** Buying point / deal name selected in builder */
  buyingPoint?: string;
  /** Blended margin % across the package */
  blendedMarginPercent?: number;
  /** Blended profit (absolute) across the package */
  blendedProfit?: number;
  /** Blended buy CPM (required for margin recalculation per schedule line) */
  blendedBuyCpm?: number;
  /** Optional costs / fee if provided by builder */
  costsFee?: number;
  /** Package-level format(s), comma-separated e.g. "15s, 30s, 40s" */
  format?: string;
  createdBy?: string;
  rows: CalculatorPackageRow[];
  createdAt?: string;
  /** Last edit time (ISO string); backend sets on update */
  updatedAt?: string | Date;
  /** Email of user who last edited; backend sets on update */
  updatedBy?: string;
}

export interface CalculatorPackageListResponse {
  count: number;
  data: CalculatorPackage[];
}

export interface GetCalculatorPackageListQuery {
  page?: number;
  limit?: number;
  sort?: string;
  order?: 1 | -1;
  channel?: string;
  /** Free-text search across name, description, channel and format. */
  search?: string;
}

export interface CreateCalculatorPackageBody {
  name: string;
  description?: string;
  channel: string;
  /** Total budget used when building the package (plan total) */
  totalBudget: number;
  hardCostsTotal: number;
  tradingDealPercentage: number;
  fxRates?: Record<string, number>;
  sellCpm: number;
  currency: string;
  buyingPoint: string;
  blendedMarginPercent: number;
  blendedProfit: number;
  blendedBuyCpm: number;
  costsFee?: number;
  /** Package format(s) as single string, comma-separated e.g. "15s, 30s, 40s" */
  format?: string;
  rows: CalculatorPackageRow[];
}

export type UpdateCalculatorPackageBody = Partial<{
  name: string;
  description: string;
  channel: string;
  /** Total budget used when building the package (plan total) */
  totalBudget: number;
  hardCostsTotal: number;
  tradingDealPercentage: number;
  fxRates: Record<string, number>;
  sellCpm: number;
  currency: string;
  buyingPoint: string;
  blendedMarginPercent: number;
  blendedProfit: number;
  blendedBuyCpm: number;
  costsFee?: number;
  /** Package format(s) as single string, comma-separated e.g. "15s, 30s, 40s" */
  format: string;
  rows: CalculatorPackageRow[];
}>;

export interface DeleteCalculatorPackageResponse {
  success: boolean;
  message: string;
}

export const calculatorPackagesApi = createApi({
  reducerPath: 'calculatorPackagesApi',
  tagTypes: [TAG.CALCULATOR_PACKAGE],
  baseQuery,
  endpoints: (builder) => ({
    getCalculatorPackageList: builder.query<
      CalculatorPackageListResponse,
      GetCalculatorPackageListQuery
    >({
      query: (params) =>
        `${CALCULATOR_PACKAGES_URL}?${buildQueryString(params as Record<string, unknown>)}`,
      transformResponse: (response: CalculatorPackageListResponse) => ({
        ...response,
        data: response.data.map((pkg) => normalizeCalculatorPackage(pkg))
      }),
      providesTags: () => [TAG.CALCULATOR_PACKAGE]
    }),
    getCalculatorPackageById: builder.query<CalculatorPackage, string>({
      query: (packageId) => `${CALCULATOR_PACKAGES_URL}/${packageId}`,
      transformResponse: (response: CalculatorPackage) => normalizeCalculatorPackage(response),
      providesTags: (_result, _error, id) => [{ type: TAG.CALCULATOR_PACKAGE, id }]
    }),
    createCalculatorPackage: builder.mutation<CalculatorPackage, CreateCalculatorPackageBody>({
      query: (body) => ({
        url: CALCULATOR_PACKAGES_URL,
        method: 'POST',
        body
      }),
      transformResponse: (response: CalculatorPackage) => normalizeCalculatorPackage(response),
      invalidatesTags: [TAG.CALCULATOR_PACKAGE]
    }),
    updateCalculatorPackage: builder.mutation<
      CalculatorPackage,
      { packageId: string; body: UpdateCalculatorPackageBody }
    >({
      query: ({ packageId, body }) => ({
        url: `${CALCULATOR_PACKAGES_URL}/${packageId}`,
        method: 'PUT',
        body
      }),
      transformResponse: (response: CalculatorPackage) => normalizeCalculatorPackage(response),
      invalidatesTags: (_result, _error, { packageId }) => [
        TAG.CALCULATOR_PACKAGE,
        { type: TAG.CALCULATOR_PACKAGE, id: packageId }
      ]
    }),
    deleteCalculatorPackage: builder.mutation<DeleteCalculatorPackageResponse, string>({
      query: (packageId) => ({
        url: `${CALCULATOR_PACKAGES_URL}/${packageId}`,
        method: 'DELETE'
      }),
      invalidatesTags: [TAG.CALCULATOR_PACKAGE]
    })
  })
});

export const {
  useGetCalculatorPackageListQuery,
  useGetCalculatorPackageByIdQuery,
  useCreateCalculatorPackageMutation,
  useUpdateCalculatorPackageMutation,
  useDeleteCalculatorPackageMutation
} = calculatorPackagesApi;
